/* Rockin' RC's — Video Feature (R6.2)
   Paired with sections/video-feature.liquid and assets/video-feature.css

   Jobs:
     1. Download nothing until the row is near the viewport. The <source> tags
        live in a data attribute and are injected on first intersection, so a
        homepage with three video rows costs zero video bytes above the fold.
     2. Play only while on screen. Off screen, or tab hidden -> pause.
     3. Start without being asked, as reliably as a browser will allow.

   On (3), three things are needed, not one. A bare play() call is the fragile
   option: Chrome, Firefox and Safari all ship per-site autoplay switches, and
   a blocked call rejects with NotAllowedError even for a muted video. So:

     a. set the autoplay property as well as calling play(), so the browser
        starts it itself once data arrives, independent of our promise;
     b. retry once on the visitor's first tap, click or keypress — that grants
        user activation and unblocks every remaining policy case;
     c. only then fall back to showing a play button.

   R6.0 note, kept because it is easy to reintroduce: scrolling past a row
   calls pause(), which rejects any in-flight play() with AbortError. That is
   our own doing and must not be treated as a failure — hence `wantPlaying`
   and the AbortError check. The 'playing' listener is the single source of
   truth for hiding the button.

   Reduced motion: these clips are muted, a few seconds long, and every row has
   a visible control to stop them, so autoplay is no longer withheld from
   visitors with the OS preference set. Ask and I'll put that gate back.

   If this script never runs, the poster image still renders. Nothing breaks.  */

(function () {
  'use strict';

  var instances = [];
  var gestureArmed = false;

  function setSoundState(btn, on) {
    if (!btn) return;
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    btn.setAttribute('aria-label', on ? 'Turn sound off' : 'Turn sound on');
  }

  function muteAllExcept(keep) {
    instances.forEach(function (i) {
      if (i.video === keep) return;
      i.video.muted = true;
      setSoundState(i.soundBtn, false);
    });
  }

  /* First real interaction anywhere on the page grants user activation, which
     lifts every autoplay block a browser has. Take one run at the rows that
     wanted to play and were told no. */
  function armGesture() {
    if (gestureArmed) return;
    gestureArmed = true;
    var retry = function () {
      instances.forEach(function (i) {
        if (i.retry) i.retry();
      });
    };
    ['pointerdown', 'touchstart', 'keydown', 'wheel'].forEach(function (evt) {
      document.addEventListener(evt, retry, { once: true, capture: true, passive: true });
    });
  }

  function init(el) {
    if (el.vfReady) return;
    var video = el.querySelector('video.vf__video');
    if (!video) return;
    el.vfReady = true;

    var playBtn = el.querySelector('[data-vf-play]');
    var soundBtn = el.querySelector('[data-vf-sound]');
    var wantsAuto = el.getAttribute('data-autoplay') === 'true';

    var inst = {
      el: el,
      video: video,
      soundBtn: soundBtn,
      loaded: false,
      userStarted: false,
      wantPlaying: false,
      onScreen: false
    };
    instances.push(inst);

    function showButton() { if (playBtn) playBtn.hidden = false; }
    function hideButton() { if (playBtn) playBtn.hidden = true; }

    function load() {
      if (inst.loaded) return;
      inst.loaded = true;
      var list = [];
      try {
        list = JSON.parse(el.getAttribute('data-sources') || '[]');
      } catch (e) {
        list = [];
      }
      if (!list.length) {
        showButton();
        return;
      }
      list.forEach(function (s) {
        if (!s || !s.src) return;
        var node = document.createElement('source');
        node.src = s.src;
        if (s.type) node.type = s.type;
        video.appendChild(node);
      });
      video.load();
    }

    function attemptPlay(fromGesture) {
      load();
      if (!video.paused) return;
      inst.wantPlaying = true;
      /* Belt: let the browser start it on its own once data arrives. */
      video.muted = true;
      video.autoplay = true;
      /* Braces: ask directly, so it starts as early as possible. */
      var p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(function (err) {
          if (!inst.wantPlaying) return;                 // we paused it ourselves
          if (err && err.name === 'AbortError') return;  // ditto, mid-flight
          el.setAttribute('data-vf-blocked', err && err.name ? err.name : 'unknown');
          if (fromGesture) {
            showButton();          // activation didn't help; it isn't going to
          } else {
            armGesture();          // wait for a tap, then try once more
          }
        });
      }
    }

    inst.retry = function () {
      if (!inst.onScreen) return;
      if (!video.paused) return;
      if (!wantsAuto && !inst.userStarted) return;
      attemptPlay(true);
    };

    function doPause() {
      inst.wantPlaying = false;
      video.autoplay = false;
      if (!video.paused) video.pause();
    }

    /* The single source of truth for the button: if it is playing, hide it. */
    video.addEventListener('playing', function () {
      hideButton();
      el.removeAttribute('data-vf-blocked');
    });
    video.addEventListener('error', showButton);

    if (!wantsAuto) showButton();

    if (playBtn) {
      playBtn.addEventListener('click', function () {
        inst.userStarted = true;
        hideButton();
        attemptPlay(true);
      });
    }

    if (soundBtn) {
      soundBtn.addEventListener('click', function () {
        var turningOn = video.muted;
        if (turningOn) muteAllExcept(video);
        video.muted = !turningOn;
        setSoundState(soundBtn, turningOn);
        if (turningOn && video.paused) {
          inst.userStarted = true;
          attemptPlay(true);
        }
      });
    }

    if (!('IntersectionObserver' in window)) {
      inst.onScreen = true;
      if (wantsAuto) attemptPlay(false);
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) load();
          var visible = entry.isIntersecting && entry.intersectionRatio >= 0.25;
          inst.onScreen = visible;
          if (visible) {
            if (wantsAuto || inst.userStarted) attemptPlay(false);
          } else {
            doPause();
            /* Scrolling away should not leave audio playing from off screen. */
            if (!video.muted) {
              video.muted = true;
              setSoundState(soundBtn, false);
            }
          }
        });
      },
      { rootMargin: '300px 0px', threshold: [0, 0.25] }
    );
    io.observe(el);
  }

  function initAll(root) {
    (root || document).querySelectorAll('[data-vf]').forEach(init);
  }

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) {
      instances.forEach(function (i) {
        i.wantPlaying = false;
        i.video.autoplay = false;
        if (!i.video.paused) i.video.pause();
      });
    } else {
      instances.forEach(function (i) {
        if (i.retry) i.retry();
      });
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  /* Theme editor re-renders a section on every settings change. */
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    instances = instances.filter(function (i) {
      return e.target.contains(i.el) === false;
    });
  });
})();
